<template>
    <div
        v-bind:class="classObject"
        style="padding: 1px">
        <div
            v-if="showAccessibilityViolations"
            class="alert-danger">
            <div
                v-for="(violation, index) in accessibilityViolations"
                v-bind:key="index">
                {{ violation }}
            </div>
        </div>
        <slot name="header" />
        <pc-loader
            v-bind:loading="loading || waiting"
            v-bind:message="waitMessage">
            <div
                v-bind:style="styleOverride"
                v-bind:class="wrapperClassObject">
                <slot name="contentHeader" />
                <template v-if="!loading">
                    <slot />
                </template>
            </div>
        </pc-loader>
        <pc-modal
            ref="errorDialog"
            v-bind:small="false">
            <template v-slot:header>
                <span class="h4 m-0">{{ errorDialogTitle }}</span>
            </template>
            <!-- eslint-disable vue/no-v-html -->
            <div
                v-if="errorDialogHtml"
                v-html="errorDialogHtml" />
            <!-- eslint-enable vue/no-v-html -->
            <template v-else>
                <!-- eslint-disable-next-line vue/singleline-html-element-content-newline -->
                <div v-bind:style="{ 'white-space': msgWhitespace }">{{ errorDialogMsg }}</div>
            </template>
            <div
                v-if="showErrorMetadata"
                class="text-body-secondary small mt-3">
                <div
                    v-if="Boolean(errorDialogCorrelationId)"
                    class="d-flex align-items-center">
                    <div>
                        {{ errorDialogCorrelationIdLabel }}
                    </div>
                    <div class="ms-2">
                        {{ errorDialogCorrelationId }}
                    </div>
                </div>
                <div
                    v-if="Boolean(errorDialogTimestamp)"
                    class="d-flex align-items-center">
                    <div>
                        {{ errorDialogTimestampLabel }}
                    </div>
                    <div class="ms-2">
                        {{ errorDialogTimestamp }}
                    </div>
                </div>
            </div>
            <template v-slot:footer>
                <pc-button
                    primary
                    v-on:click="closeErrorDialog">
                    {{ okText }}
                </pc-button>
            </template>
        </pc-modal>
        <pc-dialog ref="dialog" />
        <div
            ref="exportVarsDiv"
            class="pc-export-vars-div d-none" />
    </div>
</template>

<script>
    import { BP } from "Responsive/breakpoint";
    import { isAtLeast } from "Responsive/manager";
    import { IS_DEBUG_BUILD, htmlUnescape } from "Util/util";
    import { NAMESPACE, KEYS } from "./i18n/pc-app-layout.i18n";
    import { NAMESPACE as COMMON_NS, KEYS as COMMON_KEYS } from "I18n/common.i18n";
    import { mapI18n } from "Util/i18n";
    import { LAYOUTS_SYMBOL } from "_Main/injectionSymbols";

    const defaultAjaxErrorDialogDetail =
        "We apologize for the inconvenience. The details of this error have been recorded for our technical team to review.";
    const defaultErrorDialogTitle = "Something went wrong";

    export default {
        inject: { layout: LAYOUTS_SYMBOL },
        props: {
            useFluidContainer: { type: Boolean, required: false, default: false },
            loading: { type: Boolean, required: false, default: false },
            waiting: { type: Boolean, required: false, default: false },
            waitMessage: { type: String, required: false, default: null },
            /* printStyling property necessary to remove flexbox styling on printable pages,
             * since Firefox has known issues with printing large flexboxes. */
            printStyling: { type: Boolean, required: false, default: false },
            hideMenuItems: { type: Boolean, required: false, default: false },
            wrapperClassOverride: {
                type: Object,
                required: false,
                default() {
                    return {};
                }
            },
            styleOverride: {
                type: Object,
                required: false,
                default() {
                    return {};
                }
            }
        },
        data() {
            const useCardClass = !window.pc_use_vertical_nav;

            return {
                errorDialogMsg: null,
                msgWhitespace: "normal",
                errorDialogHtml: null,
                errorDialogTitle: null,
                errorDialogTimestamp: null,
                errorDialogCorrelationId: null,
                accessibilityViolations: [],
                classDefault: {
                    // TODO: fix the classic theme properly instead of adding the card class
                    card: useCardClass,
                    "m-1": true,
                    "p-3": true,
                    "d-print-block": this.printStyling
                }
            };
        },
        computed: {
            showAccessibilityViolations() {
                return IS_DEBUG_BUILD && this.accessibilityViolations && this.accessibilityViolations.length > 0;
            },
            showErrorMetadata() {
                return this.errorDialogCorrelationId || this.errorDialogTimestamp;
            },
            classObject() {
                const isXs = !isAtLeast(null, BP.sm);
                return {
                    container: !this.useFluidContainer,
                    "container-fluid": this.useFluidContainer,
                    "p-0": isXs
                };
            },
            wrapperClassObject() {
                return { ...this.classDefault, ...this.wrapperClassOverride };
            },
            ...mapI18n({ ...KEYS }, NAMESPACE),
            ...mapI18n({ ...COMMON_KEYS }, COMMON_NS)
        },
        created() {
            this.layout.app = this;
        },
        methods: {
            showErrorDialog(title, message, showHtml, preserveSpace, errorMetadata) {
                message = message || defaultAjaxErrorDialogDetail;
                if (showHtml) {
                    this.errorDialogMsg = null;
                    this.errorDialogHtml = message;
                } else {
                    this.errorDialogMsg = htmlUnescape(message);
                    this.errorDialogHtml = null;
                }
                this.msgWhitespace = preserveSpace ? "pre-wrap" : "normal";
                this.errorDialogTitle = title || defaultErrorDialogTitle;
                this.setErrorMetadata(errorMetadata);

                if (this.errorDialogPromiseWrapper) {
                    this.errorDialogPromiseWrapper.reject();
                }
                this.errorDialogPromiseWrapper = { promise: null, reject: null, resolve: null };
                this.errorDialogPromiseWrapper.promise = new Promise((_resolve, _reject) => {
                    this.errorDialogPromiseWrapper.reject = _reject;
                    this.errorDialogPromiseWrapper.resolve = _resolve;
                });
                this.$refs.errorDialog.show();
                return this.errorDialogPromiseWrapper.promise;
            },
            closeErrorDialog() {
                this.errorDialogPromiseWrapper.resolve();
                this.$refs.errorDialog.hide();
            },
            showGenericDialog(options) {
                options.testRole ??= "defaultDialog";
                return this.$refs.dialog.show(options);
            },
            closeGenericDialog(options) {
                options = options || {
                    resolve: true,
                    resolveValue: null
                };
                this.$refs.dialog.hide(options);
            },
            getExportedCssVars() {
                const exportVarsDiv = this.$refs.exportVarsDiv;
                let exportedString = window.getComputedStyle(exportVarsDiv, ":after").content;
                if (exportedString && exportedString.length > 0) {
                    exportedString = exportedString.replace(/"/g, "");
                    exportedString = exportedString.replace(/&qt;/g, '"');
                    const exportedObj = JSON.parse(exportedString);
                    return exportedObj;
                }
                return {};
            },
            registerAccessibilityViolation(violation) {
                if (IS_DEBUG_BUILD) {
                    console.error("Accessibility violation: " + violation);
                }
                this.accessibilityViolations.push(violation);
            },
            setErrorMetadata({ correlationId = null, timestamp = null } = {}) {
                this.errorDialogCorrelationId = correlationId;
                this.errorDialogTimestamp = timestamp;
            }
        }
    };
</script>
